import { useState, useEffect } from 'react';
import { getMyGroups, getAllGroups, createGroup, joinGroup, type GroupMembership, type Group } from '../lib/api/groups';

export function Groups() {
  const [myGroups, setMyGroups] = useState<GroupMembership[]>([]);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
  }, []);

  async function loadGroups() {
    try {
      setLoading(true);
      const [my, all] = await Promise.all([
        getMyGroups(),
        getAllGroups(),
      ]);
      setMyGroups(my);
      setAllGroups(all);
    } catch (err) {
      setError('Failed to load groups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateGroup(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createGroup({
        name: newGroupName,
        description: newGroupDescription || undefined,
      });
      setNewGroupName('');
      setNewGroupDescription('');
      setShowCreateForm(false);
      loadGroups();
    } catch (err) {
      setError('Failed to create group');
      console.error(err);
    }
  }

  async function handleJoinGroup(groupId: string) {
    try {
      await joinGroup(groupId);
      loadGroups();
    } catch (err) {
      setError('Failed to join group');
      console.error(err);
    }
  }

  function getGroupStatus(groupId: string): 'not-member' | 'pending' | 'approved' {
    const membership = myGroups.find(g => g.id === groupId);
    if (!membership) return 'not-member';
    return membership.status === 'approved' ? 'approved' : 'pending';
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading groups...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-background px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Groups</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium"
          >
            Create Group
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-scroll px-4 py-4 pb-24" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive rounded-lg text-destructive text-sm">
            {error}
          </div>
        )}

        {/* Create Group Form */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-background rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold mb-4">Create New Group</h2>
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    id="description"
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewGroupName('');
                      setNewGroupDescription('');
                    }}
                    className="flex-1 px-4 py-2 border border-input rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* My Groups */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-3">My Groups</h2>
          {myGroups.length === 0 ? (
            <p className="text-muted-foreground text-sm">You haven't joined any groups yet.</p>
          ) : (
            <div className="space-y-3">
              {myGroups.map((group) => (
                <div
                  key={group.id}
                  className="p-4 border border-border rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold">{group.name}</h3>
                      {group.description && (
                        <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                      )}
                      <div className="flex gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          group.status === 'approved'
                            ? 'bg-green-500/10 text-green-500'
                            : 'bg-yellow-500/10 text-yellow-500'
                        }`}>
                          {group.status === 'approved' ? 'Member' : 'Pending'}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-500">
                          {group.role === 'admin' ? 'Admin' : 'Member'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All Groups */}
        <div>
          <h2 className="text-lg font-bold mb-3">All Groups</h2>
          {allGroups.length === 0 ? (
            <p className="text-muted-foreground text-sm">No groups available.</p>
          ) : (
            <div className="space-y-3">
              {allGroups.map((group) => {
                const status = getGroupStatus(group.id);
                return (
                  <div
                    key={group.id}
                    className="p-4 border border-border rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold">{group.name}</h3>
                        {group.description && (
                          <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                        )}
                      </div>
                      <div>
                        {status === 'not-member' && (
                          <button
                            onClick={() => handleJoinGroup(group.id)}
                            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-lg"
                          >
                            Join
                          </button>
                        )}
                        {status === 'pending' && (
                          <span className="text-xs px-2 py-1 rounded bg-yellow-500/10 text-yellow-500">
                            Pending
                          </span>
                        )}
                        {status === 'approved' && (
                          <span className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-500">
                            Joined
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
